import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Collapse, FormControl, MenuItem, Select } from '@mui/material';
import { format } from 'date-fns';

import type {
    ReportConfig,
    ReportDateSettings,
    ReportProps,
    ChartReportPart,
    ReportWrapperRefAPI,
} from 'c-reports/Types';
import { ReportDateTimeframe, ReportType, PartType } from 'c-reports/Types';
import { ReportWrapper } from 'c-reports/Components';

import type {
    CampaignNoticeKey,
    ElasticSearchAvailableFilters,
    ElasticSearchFilters,
    ElasticSearchGroupByFieldResponse,
} from 'c-sdk';
import { EsPCAReportField, PCAReportField, PCAReportMetric } from 'c-sdk';
import apiClient from 'c-data/apiClient';
import { useTheme } from '@mui/styles';
import type { ReportTabProps } from 'c-main/Components/Campaign/CampaignReports/types';
import { performanceStatsToOldCampaignStats } from 'c-main/Components/Campaign/CampaignReports/ElasticSearch/Transformers';
import { Translate, useCommonTranslation } from 'c-translation';
import { YAxis } from 'recharts';
import { fieldBackdropColor, formatCompactLower } from 'c-lib';
import type { RowStats } from './energyUsageUtils';
import {
    foldDspGroupLabel,
    foldGroupedDspStats,
    getGroupKey,
    getOwnerFormatLabel,
    isGroupedStatsLeaf,
    isObject,
    DSP_AUDIENCE_FILTER_KEY,
    DSP_NO_AUDIENCE_VALUE,
    pickActualByMetric,
    pickAuditMultipliersPayload,
    pickAverageAudience,
    pickDeliveredImpacts,
    pickGeopathAudience,
    pickPlays,
    pickGroupedPayload,
    pickMultipliersPayload,
} from './energyUsageUtils';

type Props = {
    campaignId: number;
    environment: any;
    source: any;
    field?: EsPCAReportField;
    fields?: EsPCAReportField[];
    metric: PCAReportMetric;
    onMetricChange?: (m: PCAReportMetric) => void;
    currentStartDate: Date;
    currentEndDate: Date;
    currentTimeframe: ReportDateTimeframe;
    dateSettings: ReportDateSettings;
    fieldFilters?: ElasticSearchFilters;
    refineFilters: boolean;
    geopathOnly: boolean;
    audienceMode?: 'standard' | 'averages';
    comparisonLabel?: string;
    comparisonPrimary?: boolean;
    onFiltersUpdated: (filters: ElasticSearchAvailableFilters) => void;
    onResponse?: ReportTabProps['onResponse'];
    additionalControls?: React.ReactNode;
    advancedFilter?: React.ReactNode;
    showAdvancedFilters?: boolean;
    foldBadKeysToOther?: boolean;
};

type OverallComparisonViewMode = 'impacts' | 'comparison' | 'plays' | 'both';

const getDefaultOverallComparisonViewMode = (showPlaysAxis: boolean): OverallComparisonViewMode =>
    showPlaysAxis ? 'impacts' : 'both';

const AnalyticsOverallCharts: React.FC<Props> = ({
    campaignId,
    environment,
    source,
    field,
    fields: explicitFields,
    metric,
    onMetricChange,
    currentStartDate,
    currentEndDate,
    currentTimeframe,
    dateSettings,
    fieldFilters,
    refineFilters,
    geopathOnly,
    audienceMode = 'standard',
    comparisonLabel = 'Comparison',
    comparisonPrimary = false,
    onFiltersUpdated,
    additionalControls,
    advancedFilter,
    showAdvancedFilters = false,
    onResponse,
    foldBadKeysToOther = false,
}) => {
    const t = useCommonTranslation();
    const isAverageAudienceMode = audienceMode === 'averages';
    const foldDsp = useCallback(
        (value: any) => (foldBadKeysToOther ? foldGroupedDspStats(value) : value),
        [foldBadKeysToOther],
    );
    const [pcaData, setPcaData] = useState<ElasticSearchGroupByFieldResponse | null>(null);
    const totalSeriesLabel = t('Modules.Main.Campaigns.Overview.Analytics.actual');
    const playsSeriesLabel = t('Modules.Main.Campaigns.Overview.Analytics.plays');
    const impactsLabel = t('Modules.Main.Campaigns.Overview.Analytics.impactsTitle');
    const singleSeriesLabel = useMemo(() => {
        if (metric === PCAReportMetric.Time) {
            return t('Modules.Main.Campaigns.Overview.Analytics.timePlayed');
        }

        return t(
            metric === PCAReportMetric.Plays
                ? 'Modules.Main.Campaigns.Overview.Analytics.plays'
                : 'Modules.Main.Campaigns.Overview.Analytics.impressions',
        );
    }, [metric, t]);

    useEffect(() => {
        onFiltersUpdated((pcaData?.filters ?? {}) as any);
    }, [pcaData?.filters, onFiltersUpdated]);

    const isMultiField = Array.isArray(explicitFields) && explicitFields.length > 1;
    const fetchData: ReportConfig<ElasticSearchGroupByFieldResponse>['fetchData'] = useCallback(
        (start, end, timeframe) => {
            const fields = isMultiField
                ? (explicitFields as EsPCAReportField[])
                : field === EsPCAReportField.DisplayOwnerFormat
                ? [EsPCAReportField.DisplayOwner, EsPCAReportField.DisplayFormat]
                : [field ?? EsPCAReportField.DisplayOwner];
            return apiClient.ReportsElasticSearch.pcaGroupByField({
                campaignId,
                showStats: true,
                startDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(start, 'yyyy-MM-dd')} 00:00:00`,
                endDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : `${format(end, 'yyyy-MM-dd')} 23:59:59`,
                fields,
                environment,
                source,
                fieldFilters,
                dataStatus: fieldFilters?.[PCAReportField.DataStatus] as CampaignNoticeKey[],
            });
        },
        [campaignId, environment, field, explicitFields, isMultiField, fieldFilters, source],
    );
    const onDataUpdated: ReportConfig<ElasticSearchGroupByFieldResponse>['onDataUpdated'] =
        useCallback(
            (data, start, end, timeframe) => {
                setPcaData(data);

                if (data?.campaign_stats && onResponse) {
                    onResponse(
                        {
                            campaignStats: performanceStatsToOldCampaignStats(data.campaign_stats),
                            filters: (data?.filters ?? {}) as any,
                        },
                        start,
                        end,
                        timeframe,
                    );
                }
            },
            [onResponse],
        );

    const auditMultipliers = useMemo(
        () => foldDsp(pickAuditMultipliersPayload(pcaData as any)),
        [pcaData, foldDsp],
    );

    const multipliers = useMemo(
        () => foldDsp(pickMultipliersPayload(pcaData as any)),
        [pcaData, foldDsp],
    );

    const hasMultipliersKey = useMemo(
        () => pickMultipliersPayload(pcaData as any) != null,
        [pcaData],
    );

    const hasAuditedAudience = useMemo(() => {
        const aud = fieldFilters?.audience_version ?? [];
        return aud.some(v => v != null && String(v).trim() !== '');
    }, [fieldFilters?.audience_version]);
    const selectedAudienceValues = useMemo(
        () =>
            (fieldFilters?.[DSP_AUDIENCE_FILTER_KEY] ?? [])
                .map(value => String(value))
                .filter(value => value !== ''),
        [fieldFilters],
    );
    const isNonAuditedAudience = useMemo(
        () =>
            selectedAudienceValues.length === 1 &&
            selectedAudienceValues[0] === DSP_NO_AUDIENCE_VALUE,
        [selectedAudienceValues],
    );
    const isAllAudiences = useMemo(
        () => selectedAudienceValues.length === 0,
        [selectedAudienceValues],
    );
    const showPlaysAxis = useMemo(
        () =>
            !geopathOnly &&
            !isAverageAudienceMode &&
            metric === PCAReportMetric.Impacts &&
            (isAllAudiences || isNonAuditedAudience) &&
            !hasAuditedAudience,
        [
            geopathOnly,
            isAverageAudienceMode,
            metric,
            isAllAudiences,
            isNonAuditedAudience,
            hasAuditedAudience,
        ],
    );
    const showComparisonSelector = useMemo(
        () => showPlaysAxis || geopathOnly || isAverageAudienceMode,
        [geopathOnly, isAverageAudienceMode, showPlaysAxis],
    );
    const defaultComparisonViewMode = useMemo(
        () => getDefaultOverallComparisonViewMode(showPlaysAxis),
        [showPlaysAxis],
    );
    const [comparisonViewMode, setComparisonViewMode] =
        useState<OverallComparisonViewMode>(defaultComparisonViewMode);
    const normalizedComparisonViewMode = useMemo<OverallComparisonViewMode>(() => {
        if (showPlaysAxis && comparisonViewMode === 'comparison') return 'plays';
        if (!showPlaysAxis && comparisonViewMode === 'plays') return 'comparison';
        return comparisonViewMode;
    }, [comparisonViewMode, showPlaysAxis]);
    const showImpactsSeries = useMemo(
        () =>
            showPlaysAxis
                ? normalizedComparisonViewMode !== 'plays'
                : normalizedComparisonViewMode !== 'comparison',
        [normalizedComparisonViewMode, showPlaysAxis],
    );
    const showComparisonSeries = useMemo(
        () => normalizedComparisonViewMode !== 'impacts',
        [normalizedComparisonViewMode],
    );
    const useSecondaryPlaysAxis = useMemo(
        () => showPlaysAxis && showImpactsSeries && showComparisonSeries,
        [showComparisonSeries, showImpactsSeries, showPlaysAxis],
    );
    const comparisonSelectorSecondaryLabel = useMemo<React.ReactNode>(
        () =>
            showPlaysAxis ? (
                <Translate path="Modules.Main.Campaigns.Overview.Analytics.plays" />
            ) : (
                comparisonLabel
            ),
        [comparisonLabel, showPlaysAxis],
    );
    const selectorBothLabel = useMemo(
        () =>
            showPlaysAxis
                ? `${impactsLabel} ${t('Modules.Main.Campaigns.Overview.Analytics.and')} ${t(
                      'Modules.Main.Campaigns.Overview.Analytics.plays',
                  )}`
                : `${impactsLabel} ${t(
                      'Modules.Main.Campaigns.Overview.Analytics.vs',
                  )} ${comparisonLabel}`,
        [comparisonLabel, impactsLabel, showPlaysAxis, t],
    );

    useEffect(() => {
        setComparisonViewMode(defaultComparisonViewMode);
    }, [
        defaultComparisonViewMode,
        field,
        geopathOnly,
        isAverageAudienceMode,
        metric,
        showPlaysAxis,
    ]);

    const rows = useMemo(() => {
        const grouped = foldDsp(pickGroupedPayload(pcaData as any));

        const toRows = (payload: any) => {
            if (!payload) return [];
            if (isObject(payload)) {
                if (isMultiField) {
                    const fieldArr = explicitFields as EsPCAReportField[];
                    const out: { key: string; stats: RowStats }[] = [];
                    const walk = (node: any, depth: number, parts: string[]) => {
                        if (!node || typeof node !== 'object' || Array.isArray(node)) return;
                        if (depth >= fieldArr.length || isGroupedStatsLeaf(node)) {
                            out.push({
                                key: parts.join(' | '),
                                stats: (node ?? {}) as RowStats,
                            });
                            return;
                        }
                        Object.entries(node).forEach(([k, v]) => {
                            walk(v, depth + 1, [...parts, String(k)]);
                        });
                    };
                    walk(payload, 0, []);
                    return out;
                }

                if (field === EsPCAReportField.DisplayOwnerFormat) {
                    return Object.entries(payload as Record<string, any>).flatMap(
                        ([key, stats]) => {
                            if (isGroupedStatsLeaf(stats)) {
                                return [
                                    {
                                        key: getOwnerFormatLabel(stats as RowStats, String(key)),
                                        stats: (stats ?? {}) as RowStats,
                                    },
                                ];
                            }

                            if (isObject(stats)) {
                                return Object.entries(stats as Record<string, any>)
                                    .filter(([, nestedStats]) => isGroupedStatsLeaf(nestedStats))
                                    .map(([nestedKey, nestedStats]) => ({
                                        key: getOwnerFormatLabel(
                                            nestedStats as RowStats,
                                            `${String(key)} - ${String(nestedKey)}`,
                                        ),
                                        stats: (nestedStats ?? {}) as RowStats,
                                    }));
                            }

                            return [];
                        },
                    );
                }

                return Object.entries(payload as Record<string, any>).map(([k, stats]) => ({
                    key:
                        String(field) === EsPCAReportField.DisplayOwnerFormat
                            ? getOwnerFormatLabel(stats as RowStats, String(k))
                            : String(k),
                    stats: (stats ?? {}) as RowStats,
                }));
            }
            if (Array.isArray(payload)) {
                return payload.map((item: any, idx: number) => ({
                    key:
                        String(field) === EsPCAReportField.DisplayOwnerFormat
                            ? getOwnerFormatLabel(
                                  item as RowStats,
                                  String(getGroupKey(field, item, idx)),
                              )
                            : String(getGroupKey(field, item, idx)),
                    stats: (item ?? {}) as RowStats,
                }));
            }
            return [];
        };

        const groupedRows = toRows(grouped);
        const auditRows = toRows(auditMultipliers);
        const multipliersRows = toRows(multipliers);

        const useAuditMultipliers = auditRows.length > 0;
        const useNonAuditedMultipliers =
            !useAuditMultipliers && hasMultipliersKey && multipliersRows.length > 0;

        const applyLabelFold = (list: { key: string; stats: RowStats }[]) =>
            foldBadKeysToOther
                ? list.map(row => ({ ...row, key: foldDspGroupLabel(row.key) }))
                : list;

        if (useAuditMultipliers) return applyLabelFold(auditRows);
        if (useNonAuditedMultipliers) return applyLabelFold(multipliersRows);
        return applyLabelFold(groupedRows);
    }, [
        pcaData,
        field,
        explicitFields,
        isMultiField,
        auditMultipliers,
        multipliers,
        hasMultipliersKey,
        foldDsp,
        foldBadKeysToOther,
    ]);

    const topRows = useMemo(() => {
        const sorted = [...rows].sort((a, b) => {
            const av =
                geopathOnly || isAverageAudienceMode
                    ? pickDeliveredImpacts(a.stats)
                    : pickActualByMetric(a.stats, metric);
            const bv =
                geopathOnly || isAverageAudienceMode
                    ? pickDeliveredImpacts(b.stats)
                    : pickActualByMetric(b.stats, metric);
            return Number(bv) - Number(av);
        });
        return sorted;
    }, [rows, geopathOnly, isAverageAudienceMode, metric]);

    const theme = useTheme();

    const { chartData, parts } = useMemo((): {
        chartData: Array<Record<string, any>>;
        parts: ChartReportPart[];
    } => {
        if (geopathOnly || isAverageAudienceMode) {
            const impactsDataKey = totalSeriesLabel;
            const comparisonDataKey = comparisonLabel;
            const isAuditedComparisonMode = comparisonPrimary;
            const impactsValueForRow = (stats: RowStats) => pickDeliveredImpacts(stats);
            const comparisonValueForRow = (stats: RowStats) =>
                comparisonPrimary
                    ? geopathOnly
                        ? pickGeopathAudience(stats)
                        : pickAverageAudience(stats)
                    : geopathOnly
                    ? pickGeopathAudience(stats)
                    : pickAverageAudience(stats);
            const primaryFill =
                isAuditedComparisonMode && showImpactsSeries && showComparisonSeries
                    ? theme.palette.grey['500']
                    : theme.palette.primary.main;
            const secondaryFill =
                (isAuditedComparisonMode && showComparisonSeries) ||
                (showComparisonSeries && !showImpactsSeries)
                    ? theme.palette.primary.main
                    : theme.palette.grey['500'];

            const data = topRows.map(r => ({
                x: r.key,
                [impactsDataKey]: impactsValueForRow(r.stats),
                [comparisonDataKey]: comparisonValueForRow(r.stats),
            }));

            const impactsPart: ChartReportPart = {
                dataKey: impactsDataKey,
                partType: PartType.Bar,
                barProps: { fill: primaryFill },
            };
            const comparisonPart: ChartReportPart = {
                dataKey: comparisonDataKey,
                partType: PartType.Bar,
                barProps: { fill: secondaryFill },
            };
            const p: ChartReportPart[] = isAuditedComparisonMode
                ? [
                      ...(showComparisonSeries ? [comparisonPart] : []),
                      ...(showImpactsSeries ? [impactsPart] : []),
                  ]
                : [
                      ...(showImpactsSeries ? [impactsPart] : []),
                      ...(showComparisonSeries ? [comparisonPart] : []),
                  ];

            return { chartData: data, parts: p };
        }

        const data = topRows.map(r => ({
            x: r.key,
            value: pickActualByMetric(r.stats, metric),
            plays: showPlaysAxis ? pickPlays(r.stats) : undefined,
        }));

        const p: ChartReportPart[] = [
            ...(showImpactsSeries
                ? [
                      {
                          dataKey: 'value',
                          partType: PartType.Bar,
                          labelOverride: singleSeriesLabel,
                          barProps: {
                              fill: theme.palette.primary.main,
                              ...(useSecondaryPlaysAxis ? { yAxisId: 'primary' } : {}),
                          },
                      } as ChartReportPart,
                  ]
                : []),
        ];

        if (showPlaysAxis && showComparisonSeries) {
            p.push({
                dataKey: 'plays',
                partType: PartType.Bar,
                labelOverride: playsSeriesLabel,
                barProps: {
                    fill: showImpactsSeries
                        ? theme.palette.grey['500']
                        : theme.palette.primary.main,
                    ...(useSecondaryPlaysAxis ? { yAxisId: 'plays' } : {}),
                },
            });
        }

        return { chartData: data, parts: p };
    }, [
        geopathOnly,
        isAverageAudienceMode,
        topRows,
        theme.palette.primary.main,
        theme.palette.grey,
        comparisonLabel,
        comparisonPrimary,
        metric,
        playsSeriesLabel,
        showPlaysAxis,
        showComparisonSeries,
        showImpactsSeries,
        singleSeriesLabel,
        totalSeriesLabel,
        useSecondaryPlaysAxis,
    ]);

    const reportProps = useMemo<ReportProps<unknown>[]>(
        () => [
            {
                type: ReportType.Bar,
                namePrefix: `${campaignId}_analytics_overall_${field ?? 'default'}_${
                    geopathOnly ? 'geopath' : isAverageAudienceMode ? 'averages' : 'standard'
                }_${metric}`,
                xAxisDataKey: 'x',
                data: chartData,
                parts,
                hideLegend: !(geopathOnly || isAverageAudienceMode),
                yAxisTickFormatterAsTime:
                    !(geopathOnly || isAverageAudienceMode) && metric === PCAReportMetric.Time,
                sliderWrapperProps: { mt: 1 },
                xAxisProps: { tick: { fontSize: 12 } },
                yAxisProps: useSecondaryPlaysAxis ? { yAxisId: 'primary' } : undefined,
                chartChildren: useSecondaryPlaysAxis
                    ? [
                          <YAxis
                              key="plays-axis"
                              yAxisId="plays"
                              orientation="right"
                              tickFormatter={value => formatCompactLower(Number(value ?? 0))}
                              tick={{ fill: theme.palette.grey['500'] }}
                              axisLine={{ stroke: theme.palette.grey['500'] }}
                              tickLine={{ stroke: theme.palette.grey['500'] }}
                          />,
                      ]
                    : undefined,
            },
        ],
        [
            campaignId,
            chartData,
            field,
            geopathOnly,
            isAverageAudienceMode,
            metric,
            parts,
            theme.palette.grey,
            useSecondaryPlaysAxis,
        ],
    );

    const beforeChartComponent = useMemo(
        () => (
            <>
                {advancedFilter && (
                    <Collapse in={showAdvancedFilters}>
                        <Box>{advancedFilter}</Box>
                    </Collapse>
                )}

                <Box sx={{ px: 1, pt: 1 }}>
                    {showComparisonSelector && (
                        <FormControl size="small">
                            <Select
                                value={normalizedComparisonViewMode}
                                onChange={e =>
                                    setComparisonViewMode(
                                        e.target.value as OverallComparisonViewMode,
                                    )
                                }
                                sx={{ backgroundColor: fieldBackdropColor, minWidth: 160 }}
                            >
                                <MenuItem value="impacts">
                                    <Translate path="Modules.Main.Campaigns.Overview.Analytics.impactsTitle" />
                                </MenuItem>
                                <MenuItem value={showPlaysAxis ? 'plays' : 'comparison'}>
                                    {comparisonSelectorSecondaryLabel}
                                </MenuItem>
                                <MenuItem value="both">{selectorBothLabel}</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </>
        ),
        [
            advancedFilter,
            comparisonSelectorSecondaryLabel,
            normalizedComparisonViewMode,
            selectorBothLabel,
            showAdvancedFilters,
            showComparisonSelector,
            showPlaysAxis,
        ],
    );
    const reportRef = useRef<ReportWrapperRefAPI>(null);

    const fieldsKey = useMemo(() => (explicitFields ?? []).join(','), [explicitFields]);
    useEffect(() => {
        reportRef.current?.refreshData?.();
    }, [fieldFilters, geopathOnly, isAverageAudienceMode, metric, field, fieldsKey]);

    return (
        <ReportWrapper
            requestKeyPrefix=""
            ref={reportRef}
            initialStartDate={currentStartDate}
            initialEndDate={currentEndDate}
            dateSettings={dateSettings}
            fetchData={fetchData}
            onDataUpdated={onDataUpdated}
            reportProps={reportProps as any}
            beforeChartComponent={beforeChartComponent}
            additionalDateControls={<>{additionalControls}</>}
            additionalDateControlsPosition="end"
            reportChartWrapperProps={{ height: '75vh' }}
            reportWrapperProps={{
                sx: {
                    bgcolor: fieldBackdropColor,
                    mt: 1,
                    pb: 1,
                    pt: 1,
                },
            }}
        />
    );
};

export default AnalyticsOverallCharts;
